'use client';

import { useState } from 'react';
import type { StockSymbol, Ticker } from '@benzinga/session';
import type { DelayedQuote } from '@benzinga/quotes-manager';
import type { TrendingTopic } from '@benzinga/trending-topics-manager';
import type { BreakingNews } from '@benzinga/basic-news-manager';
import type { Section, SectionLink, PageContext } from '../../entities';
import type { PremiumServiceAccess, PremiumServiceKey } from '../../premiumServiceLinks';
import { sectionMenus as defaultSectionMenus } from '../../section-menu-data';
import { SectionNav } from './SectionNav';
import { QuotesAndAccountBar } from './QuotesAndAccountBar';
import { MobileMenu } from './MobileMenu';
import { NavBanners } from './NavBanners';
import styles from './SiteNav.module.scss';

export interface SiteNavProps {
  variant?: 'default' | 'dark';
  sticky?: boolean;
  sections?: Section[];
  pageContext?: PageContext;
  premiumAccess: PremiumServiceAccess;
  isLoggedIn: boolean;
  onLogin: () => void;
  onRegister?: () => void;
  onLogout: () => void;
  quotes?: Record<StockSymbol, DelayedQuote> | null;
  marketTickers?: string[];
  recentTickers?: string[];
  initialTrendingTickers?: Ticker[];
  initialTrendingTopics?: TrendingTopic[];
  breakingNews?: BreakingNews[];
  hideBanner?: boolean;
  hideQuotesAndAccountBar?: boolean;
  hideTickerSearch?: boolean;
  hidePremium?: boolean;
  showHeaderDisplayAd?: boolean;
  expandedHeaderDisplayAd?: boolean;
  showRotatingBanner?: boolean;
  onSectionOpen?: (section: Section) => void;
  onSectionLinkClick?: (section: Section, link: SectionLink) => void;
  onPremiumClick?: (service: PremiumServiceKey) => void;
  onOpenMobileMenu?: () => void;
  navChromeReady?: boolean;
}

export const SiteNav = ({
  variant = 'default',
  sticky = true,
  sections,
  pageContext,
  premiumAccess,
  isLoggedIn,
  onLogin,
  onRegister,
  onLogout,
  quotes,
  marketTickers,
  recentTickers,
  initialTrendingTickers,
  initialTrendingTopics,
  breakingNews,
  hideBanner,
  hideQuotesAndAccountBar = false,
  hideTickerSearch = false,
  hidePremium = false,
  showHeaderDisplayAd,
  expandedHeaderDisplayAd,
  showRotatingBanner,
  onSectionOpen,
  onSectionLinkClick,
  onPremiumClick,
  onOpenMobileMenu,
  navChromeReady = false,
}: SiteNavProps) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const resolvedSections = sections ?? defaultSectionMenus;

  return (
    <div className={styles.root} data-variant={variant} data-sticky={sticky ? undefined : 'false'}>
      <NavBanners
        breakingNews={breakingNews}
        hideBanner={hideBanner}
        showHeaderDisplayAd={showHeaderDisplayAd}
        expandedHeaderDisplayAd={expandedHeaderDisplayAd}
        showRotatingBanner={showRotatingBanner}
      />
      {!hideQuotesAndAccountBar && (
        <QuotesAndAccountBar
          isLoggedIn={isLoggedIn}
          onLogin={onLogin}
          onRegister={onRegister}
          onLogout={onLogout}
          premiumAccess={premiumAccess}
          quotes={quotes}
          marketTickers={marketTickers}
          recentTickers={recentTickers}
          navChromeReady={navChromeReady}
        />
      )}
      <SectionNav
        sections={resolvedSections}
        variant={variant}
        premiumAccess={premiumAccess}
        pageContext={pageContext}
        initialTrendingTickers={initialTrendingTickers}
        initialTrendingTopics={initialTrendingTopics}
        mobileMenuOpen={mobileOpen}
        onSectionOpen={onSectionOpen}
        onSectionLinkClick={onSectionLinkClick}
        hideTickerSearch={hideTickerSearch}
        hidePremium={hidePremium}
        onPremiumClick={onPremiumClick}
        onOpenMobileMenu={() => {
          setMobileOpen(current => !current);
          onOpenMobileMenu?.();
        }}
      />
      <MobileMenu
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        sections={resolvedSections}
        variant={variant}
        premiumAccess={premiumAccess}
        pageContext={pageContext}
        initialTrendingTickers={initialTrendingTickers}
        initialTrendingTopics={initialTrendingTopics}
        onSectionOpen={onSectionOpen}
        onSectionLinkClick={onSectionLinkClick}
        onPremiumClick={onPremiumClick}
        hideTickerSearch={hideTickerSearch}
        hidePremium={hidePremium}
      />
    </div>
  );
};
